import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchConversations,
  fetchConversation,
  fetchConversationMessages,
  sendWhatsAppMessage,
} from '../WhatsApp-Marketing/shared/whatsappApi.js';

// Server state for the WhatsApp Live Inbox. The list and the open chat are polled on an
// interval by the page, so the reducers must be safe to re-run repeatedly.

const asMessage = (error, fallback) => error?.message || fallback;

// One row per customer number, with its owner and status; `view` is mine / unassigned / all.
export const loadChats = createAsyncThunk(
  'whatsappInbox/loadChats',
  async ({ view = 'all', status = '', q = '' } = {}, { rejectWithValue }) => {
    try {
      return await fetchConversations({ view, status, q });
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load chats'));
    }
  }
);

// The open chat's messages. Someone who doesn't own the chat gets only the latest few
// (`partial`), plus how many are hidden.
export const loadThread = createAsyncThunk(
  'whatsappInbox/loadThread',
  async (phone, { rejectWithValue }) => {
    if (!phone) return { messages: [], partial: false, hidden: 0 };
    try {
      return await fetchConversationMessages(phone);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load the chat'));
    }
  }
);

// Owner, status, internal notes and the handover history of the open chat.
export const loadConversation = createAsyncThunk(
  'whatsappInbox/loadConversation',
  async (phone, { rejectWithValue }) => {
    if (!phone) return null;
    try {
      const { conversation } = await fetchConversation(phone);
      return conversation;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load the chat details'));
    }
  }
);

// Free text inside the 24-hour window, or an approved template ({ templateName, languageCode, variables }).
export const sendReply = createAsyncThunk(
  'whatsappInbox/sendReply',
  async ({ to, text, template }, { dispatch, rejectWithValue }) => {
    try {
      await sendWhatsAppMessage(template ? { to, ...template } : { to, text });
      await Promise.all([dispatch(loadThread(to)), dispatch(loadConversation(to))]);
      return { to };
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to send'));
    }
  }
);

const initialState = {
  chats: [],
  counts: { mine: 0, unassigned: 0, all: 0 },
  activePhone: null,
  messages: [],
  partial: false,
  hidden: 0,
  conversation: null,
  loadingChats: true,
  sending: false,
  error: null,
  sendError: null,
};

const whatsappInboxSlice = createSlice({
  name: 'whatsappInbox',
  initialState,
  reducers: {
    setActivePhone: (state, action) => {
      if (state.activePhone === action.payload) return;
      state.activePhone = action.payload;
      state.messages = [];
      state.partial = false;
      state.hidden = 0;
      state.conversation = null;
      state.sendError = null;
    },
    setConversation: (state, action) => {
      state.conversation = action.payload;
    },
    clearInboxError: (state) => {
      state.error = null;
      state.sendError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadChats.fulfilled, (state, action) => {
        state.loadingChats = false;
        state.chats = action.payload?.conversations || [];
        state.counts = action.payload?.counts || state.counts;
        state.error = null;
        // Open the newest chat on first load only; later polls must not move the user
        // off the chat they are reading.
        if (!state.activePhone) state.activePhone = state.chats[0]?.phone || null;
      })
      .addCase(loadChats.rejected, (state, action) => {
        state.loadingChats = false;
        state.error = action.payload;
      })
      .addCase(loadThread.fulfilled, (state, action) => {
        // A slow answer for a chat the user has already left is dropped.
        if (action.meta.arg !== state.activePhone) return;
        state.messages = action.payload.messages || [];
        state.partial = Boolean(action.payload.partial);
        state.hidden = action.payload.hidden || 0;
      })
      .addCase(loadConversation.fulfilled, (state, action) => {
        if (action.meta.arg !== state.activePhone) return;
        state.conversation = action.payload;
      })
      .addCase(sendReply.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendReply.fulfilled, (state) => {
        state.sending = false;
      })
      .addCase(sendReply.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload;
      });
  },
});

export const { setActivePhone, setConversation, clearInboxError } = whatsappInboxSlice.actions;

export default whatsappInboxSlice.reducer;
